import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HardHat, ArrowRight } from "lucide-react";
import { BrandMarkDark } from "../components/BrandMark";

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [invite, setInvite] = useState(null);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({ password: "", name: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get(`/auth/invite/${token}`).then((r) => setInvite(r.data)).catch((e) => setErr(e.response?.data?.detail || "Pozvánka neplatná"));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setErr("");
        try {
            const { data } = await api.post("/auth/accept-invite", { token, password: form.password, name: form.name });
            setUser(data);
            navigate("/");
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Akce selhala");
        } finally { setLoading(false); }
    };

    if (err && !invite) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="bg-white border border-slate-200 p-8 max-w-md text-center">
                <h1 className="font-display text-2xl font-black text-slate-900 mb-2">Pozvánka neplatná</h1>
                <p className="text-sm text-slate-600 mb-4">{err}</p>
                <button onClick={() => navigate("/prihlaseni")} className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Přejít na přihlášení</button>
            </div>
        </div>
    );

    if (!invite) return <div className="min-h-screen flex items-center justify-center text-slate-500 uppercase text-xs tracking-widest">Načítání…</div>;

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="w-full max-w-md bg-white border border-slate-200 sharp-shadow p-8" data-testid="accept-invite-page">
                <div className="mb-6">
                    <BrandMarkDark tagline="Pozvánka do firmy" />
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-600 px-4 py-3 mb-6 text-sm">
                    Byli jste pozváni do firmy <b>{invite.company_name}</b> v roli <b>{invite.role === "admin" ? "administrátor" : "vedoucí technik"}</b>.
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="label-eyebrow block mb-2">Email</label>
                        <input type="email" inputMode="email" value={invite.email} disabled className="w-full px-4 py-3 border border-slate-300 bg-slate-100 text-slate-500 text-sm rounded-sm" />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-2">Jméno a příjmení *</label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="accept-name"
                               className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-2">Heslo *</label>
                        <input required minLength={10} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="accept-password"
                               className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                    </div>

                    {err && <div className="border-l-4 border-red-600 bg-red-50 px-4 py-2 text-sm text-red-700">{err}</div>}

                    <button type="submit" disabled={loading} data-testid="accept-submit"
                            className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm rounded-sm disabled:opacity-50">
                        {loading ? "Vytvářím účet…" : "Přijmout pozvánku"} <ArrowRight className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}
